export type CatalogoBannerEstado = 'inicia' | 'vigente' | 'finalizado';

export type CatalogoBannerParColores = {
  fondo: string;
  texto: string;
  mensaje: string;
};

export type CatalogoBannerColores = Record<CatalogoBannerEstado, CatalogoBannerParColores>;

export const CATALOGO_BANNER_MENSAJE_MAX = 120;

export const DEFAULT_CATALOGO_BANNER_COLORES: CatalogoBannerColores = {
  inicia: {
    fondo: '#FECA03',
    texto: '#030213',
    mensaje: 'Este evento inicia en {fecha}',
  },
  vigente: {
    fondo: '#FECA03',
    texto: '#030213',
    mensaje: 'Evento vigente. Termina en {fecha}',
  },
  finalizado: {
    fondo: '#A20000',
    texto: '#FFFFFF',
    mensaje: 'Evento finalizado el {fecha}',
  },
};

export const CATALOGO_BANNER_ESTADOS: Array<{
  id: CatalogoBannerEstado;
  label: string;
  hint: string;
}> = [
  { id: 'inicia', label: 'Por iniciar', hint: 'Antes de la fecha de inicio' },
  { id: 'vigente', label: 'Vigente', hint: 'Entre la fecha de inicio y la de fin' },
  { id: 'finalizado', label: 'Finalizado', hint: 'Después de la fecha de fin' },
];

const MESES_ES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
] as const;

function normalizeHexColor(raw: unknown, fallback: string): string {
  const s = String(raw ?? '').trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s.toUpperCase();
  if (/^#[0-9A-Fa-f]{3}$/.test(s)) {
    const h = s.slice(1);
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toUpperCase();
  }
  return fallback;
}

const LEGACY_MENSAJE_INICIA = 'Este evento inicia el {fecha}';
const LEGACY_MENSAJE_VIGENTE = 'Evento vigente hasta el {fecha}';

function normalizeMensaje(
  raw: unknown,
  fallback: string,
  estado?: CatalogoBannerEstado
): string {
  const s = String(raw ?? '').replace(/\s+/g, ' ').trim();
  if (!s) return fallback;
  if (estado === 'inicia' && s === LEGACY_MENSAJE_INICIA) return fallback;
  if (estado === 'vigente' && s === LEGACY_MENSAJE_VIGENTE) return fallback;
  return s.slice(0, CATALOGO_BANNER_MENSAJE_MAX);
}

function parseParEstado(
  raw: unknown,
  estado: CatalogoBannerEstado
): CatalogoBannerParColores {
  const fallback = DEFAULT_CATALOGO_BANNER_COLORES[estado];
  const o = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  return {
    fondo: normalizeHexColor(o.fondo, fallback.fondo),
    texto: normalizeHexColor(o.texto, fallback.texto),
    mensaje: normalizeMensaje(o.mensaje, fallback.mensaje, estado),
  };
}

export function parseCatalogoBannerColores(raw: unknown): CatalogoBannerColores {
  let obj: Record<string, unknown> = {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        obj = parsed as Record<string, unknown>;
      }
    } catch {
      obj = {};
    }
  } else if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    obj = raw as Record<string, unknown>;
  }
  return {
    inicia: parseParEstado(obj.inicia, 'inicia'),
    vigente: parseParEstado(obj.vigente, 'vigente'),
    finalizado: parseParEstado(obj.finalizado, 'finalizado'),
  };
}

export function catalogoBannerColoresDeEstado(
  colores: CatalogoBannerColores | null | undefined,
  estado: CatalogoBannerEstado
): CatalogoBannerParColores {
  const parsed = parseCatalogoBannerColores(colores);
  return parsed[estado] ?? DEFAULT_CATALOGO_BANNER_COLORES[estado];
}

/** Fecha YYYY-MM-DD → «30 de Octubre», sin desfase UTC. */
export function formatFechaBannerLegible(ymd: string | null | undefined): string {
  const s = String(ymd ?? '').trim().slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return '…';
  const dia = Number(m[3]);
  const mesIdx = Number(m[2]) - 1;
  if (!Number.isFinite(dia) || mesIdx < 0 || mesIdx > 11) return '…';
  return `${dia} de ${MESES_ES[mesIdx]}`;
}

/** Sustituye `{fecha}` en la plantilla del banner. */
export function interpolarMensajeBanner(
  plantilla: string | null | undefined,
  fechaLegible: string
): string {
  const t = String(plantilla ?? '').trim();
  const base = t.length > 0 ? t : '{fecha}';
  return base.replace(/\{fecha\}/gi, fechaLegible);
}
